
/**
 * Accessibility utility functions and constants.
 * 
 * --- Accessibility / unique ID patterns ---
 * 
 * 1. React `useId` Hook (Standard React Pattern):
 *    For functional React components, you should prefer React's native `useId` hook.
 *    It generates stable, unique IDs that are consistent across both client and server rendering
 *    (SSR), avoiding HTML hydration mismatch issues.
 * 
 *    Example:
 *    ```tsx
 *    import { useId } from 'react';
 * 
 *    const MyInputField = ({ label }) => {
 *      const id = useId();
 *      const descriptionId = `${id}-description`;
 * 
 *      return (
 *        <div>
 *          <label htmlFor={id}>{label}</label>
 *          <input id={id} aria-describedby={descriptionId} />
 *          <span id={descriptionId}>Helper text</span>
 *        </div>
 *      );
 *    };
 *    ```
 * 
 * 2. Static / Plain Utility `generateFieldId` (Generic Pattern):
 *    For non-hook utility files, static functions, legacy class components, or places where hooks
 *    cannot be used, use the `generateFieldId` utility function.
 */

// Generate unique field IDs
export const generateFieldId = (name?: string, prefix: string = 'field'): string => {
  return name ? `${prefix}-${name}` : `${prefix}-${Math.random().toString(36).substring(2, 11)}`;
};

export const generateStarRatingLabel = (rating: number, max = 5) => `${rating} out of ${max} stars`;

// aria-label utility for common button purposes
export const ICON_BUTTON_LABELS = {
  CLEAR: 'Clear field',
  CLOSE: 'Close dialog',
  OPEN_PICKER: 'Open',
  TOGGLE: 'Toggle',
  SEARCH: 'Search',
  FILTER: 'Filter',
  EDIT: 'Edit',
  DELETE: 'Delete',
  CALENDAR: 'Select date',
  NEXT: 'Next',
  PREV: 'Previous',
  MENU: 'Open menu',
} as const;

